/**
 * Maverick Test Panel — DEV-ONLY surface.
 *
 * Visibility rules (BOTH must allow):
 *   • import.meta.env.DEV  — tree-shakes out of production bundles.
 *   • OR ?maverick_debug=1 query param — escape hatch for staging QA.
 *
 * Beta users must NEVER see this surface. It exposes seeded scenarios and
 * implementation detail that would undermine trust.
 *
 * Reads capability metadata from getMaverickCapabilities() so the test
 * checklist can never claim a capability the engine doesn't have.
 *
 * Seed scenario inventory is hand-maintained here because it documents
 * intent ("which seed item targets which capability"), not engine state.
 * If you add a new seed, add a row here.
 */

import { useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FlaskConical } from "lucide-react";
import {
  getMaverickCapabilities,
  CLOSE_MODE_LABELS,
  type MaverickCapability,
} from "@/data/maverick/capabilities";

/** Returns true when this DEV-only surface is allowed to render. */
export function isMaverickDebugEnabled(): boolean {
  if (import.meta.env.DEV) return true;
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("maverick_debug") === "1";
  } catch {
    return false;
  }
}

interface SeedScenario {
  id: string;
  file: string;
  role: string;
  capabilityKey: string; // matches MaverickCapability.key
  description: string;
  reproduce: string;
}

/**
 * Hand-authored map of seeded test fixtures → engine capabilities.
 * Update this when you add or remove seed records.
 */
const SEEDED_SCENARIOS: SeedScenario[] = [
  {
    id: "AQ-MAV-001",
    file: "src/data/actionQueue/seedMaverickQueue.ts",
    role: "ops_manager",
    capabilityKey: "stalled_queue_item",
    description: "Queue item open 5 days, owned by ops_manager, on JOB-004.",
    reproduce: "Sign in as Ops Manager → open Maverick → primary slot.",
  },
  {
    id: "AQ-MAV-002",
    file: "src/data/actionQueue/seedMaverickQueue.ts",
    role: "ops_manager",
    capabilityKey: "duplicate_attention_on_same_job",
    description: "Open queue item on JOB-005, paired with FU-MAV-001 handoff on same job.",
    reproduce: "Sign in as Ops Manager → assist strip surfaces convergence rec.",
  },
  {
    id: "AQ-MAV-003",
    file: "src/data/actionQueue/seedMaverickQueue.ts",
    role: "any",
    capabilityKey: "unowned_work",
    description: "Queue item with no assignedToRoleKey/UserId, on JOB-008.",
    reproduce: "Open Maverick — surfaces in primary unowned-work slot.",
  },
  {
    id: "FU-MAV-001",
    file: "src/data/seedFollowUpTasks.ts",
    role: "ops_manager",
    capabilityKey: "duplicate_attention_on_same_job",
    description: "Pending handoff on JOB-005 paired with AQ-MAV-002 to drive convergence.",
    reproduce: "Already routed; visible in Routed filter and convergence rec.",
  },
  {
    id: "FU-MAV-002",
    file: "src/data/seedFollowUpTasks.ts",
    role: "ops_manager",
    capabilityKey: "handoff_without_followthrough",
    description: "Completed handoff with no linked queue item, older than 6h threshold.",
    reproduce: "Open Maverick → assist strip observational rec (no actionable route).",
  },
];

export function MaverickTestPanel() {
  const [filter, setFilter] = useState<"all" | "covered" | "uncovered">("all");

  const capabilities = useMemo(() => getMaverickCapabilities(), []);

  // Build coverage map: capability.key → seeds that target it.
  const coverage = useMemo(() => {
    const m = new Map<string, SeedScenario[]>();
    for (const s of SEEDED_SCENARIOS) {
      const arr = m.get(s.capabilityKey) ?? [];
      arr.push(s);
      m.set(s.capabilityKey, arr);
    }
    return m;
  }, []);

  const visible = useMemo(() => {
    if (filter === "all") return capabilities;
    if (filter === "covered") return capabilities.filter((c) => coverage.has(c.key));
    return capabilities.filter((c) => !coverage.has(c.key));
  }, [capabilities, coverage, filter]);

  const coveredCount = capabilities.filter((c) => coverage.has(c.key)).length;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-[11px] gap-1.5 border-dashed border-warning/50 text-warning dark:text-warning hover:bg-warning/10"
        >
          <FlaskConical className="h-3 w-3" />
          Test Maverick
          <Badge variant="outline" className="text-[9px] h-4 px-1 ml-1 border-warning/40">
            DEV
          </Badge>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-warning" />
            Test Maverick
            <Badge variant="outline" className="text-[10px] h-5 border-warning/40 text-warning dark:text-warning">
              DEV ONLY
            </Badge>
          </SheetTitle>
          <SheetDescription>
            Engine-derived capability list with the seeded fixtures that exercise
            each one. Not visible to beta users.
          </SheetDescription>
        </SheetHeader>

        {/* ── Coverage summary ── */}
        <section className="mt-6 rounded-lg border bg-muted/20 p-4 text-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold">Capability coverage</span>
            <span className="font-mono text-muted-foreground">
              {coveredCount} / {capabilities.length}
            </span>
          </div>
          <div className="flex gap-1.5">
            {(["all", "covered", "uncovered"] as const).map((f) => (
              <Button
                key={f}
                variant={filter === f ? "default" : "outline"}
                size="sm"
                className="h-6 px-2 text-[10px] capitalize"
                onClick={() => setFilter(f)}
              >
                {f}
              </Button>
            ))}
          </div>
        </section>

        {/* ── Capability rows ── */}
        <section className="mt-6 space-y-3">
          {visible.map((cap) => (
            <CapabilityRow key={cap.key} cap={cap} seeds={coverage.get(cap.key) ?? []} />
          ))}
          {visible.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-8">
              No capabilities match this filter.
            </p>
          )}
        </section>
      </SheetContent>
    </Sheet>
  );
}

function CapabilityRow({ cap, seeds }: { cap: MaverickCapability; seeds: SeedScenario[] }) {
  const covered = seeds.length > 0;
  return (
    <div className="rounded-md border bg-card p-3 text-xs">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-medium">{cap.name}</div>
        <div className="flex gap-1 shrink-0">
          <Badge variant="outline" className="text-[9px] h-4 px-1.5 capitalize">
            {cap.engine}
          </Badge>
          {!cap.primarySurface && (
            <Badge variant="outline" className="text-[9px] h-4 px-1.5">
              trend
            </Badge>
          )}
          <Badge
            variant={covered ? "default" : "outline"}
            className={covered ? "text-[9px] h-4 px-1.5" : "text-[9px] h-4 px-1.5 border-dashed"}
          >
            {covered ? `${seeds.length} seed${seeds.length > 1 ? "s" : ""}` : "uncovered"}
          </Badge>
        </div>
      </div>
      <p className="text-muted-foreground leading-relaxed mb-1.5">{cap.description}</p>
      <div className="text-[10px] text-muted-foreground/80 space-y-0.5">
        <div><span className="font-semibold">Trigger:</span> {cap.trigger}</div>
        <div><span className="font-semibold">Closes via:</span> {CLOSE_MODE_LABELS[cap.closeMode]}</div>
      </div>
      {seeds.length > 0 && (
        <div className="mt-2 pt-2 border-t space-y-1.5">
          {seeds.map((s) => (
            <div key={s.id} className="text-[10px]">
              <div className="flex items-center gap-1.5">
                <code className="font-mono bg-muted px-1 py-0.5 rounded text-[9px]">{s.id}</code>
                <span className="text-muted-foreground">· role: {s.role}</span>
              </div>
              <div className="text-muted-foreground/90 mt-0.5">{s.description}</div>
              <div className="text-muted-foreground/70 mt-0.5 italic">→ {s.reproduce}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
